/**
 * UserList.js
 * Component for displaying and managing the list of users/athletes
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Form, 
  Row, 
  Col, 
  Badge, 
  Pagination,
  Alert,
  Spinner,
  Dropdown
} from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { userService, commonService, roleService, authService, portalService } from '../../services/api';
import { ROLE_BADGE_COLORS, USER_ROLES, SYSTEM_ROLE_LABELS } from '../../utils/constants';
import BulkUserActions from './BulkUserActions';
import { exportToCSV } from '../../utils/csvExport';
import { isMinor } from '../../utils/userHelpers';

const parseConsentFilter = (filterValue) => {
  if (!filterValue) return { consentId: '', status: '' };
  if (filterValue === 'mandatory_completed') {
    return { consentId: 'mandatory', status: 'completed' };
  }
  if (filterValue === 'mandatory_missing' || filterValue === 'mandatory_incomplete') {
    return { consentId: 'mandatory', status: 'missing' };
  }
  if (filterValue.startsWith('consent_')) {
    const parts = filterValue.split('_');
    if (parts.length >= 3) {
      return { consentId: parts[1], status: parts[2] };
    }
  }
  if (filterValue === 'media_accepted') {
    return { consentId: 'media', status: 'accepted' };
  }
  if (filterValue === 'media_rejected' || filterValue === 'media_missing') {
    return { consentId: 'media', status: 'rejected' };
  }
  return { consentId: '', status: '' };
};

const UserList = () => {
  const isCurrentUserAdmin = authService?.hasRole ? authService.hasRole(USER_ROLES.ADMIN) : false;
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const initialAcademicYearParam = searchParams.get('academicYear');
  const initialAcademicYear = initialAcademicYearParam && initialAcademicYearParam !== 'all' && !Number.isNaN(parseInt(initialAcademicYearParam, 10))
    ? parseInt(initialAcademicYearParam, 10)
    : '';
  const hasInitializedYearRef = useRef(false);

  // Filters inizializzati dai parametri URL (per ripristinare stato al back)
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    role: searchParams.get('role') || '',
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: parseInt(searchParams.get('limit') || '20', 10),
    sortBy: searchParams.get('sortBy') || 'alphabetical',
    lastNameStartsWith: searchParams.get('letter') || '',
    academicYear: initialAcademicYear,
    consentFilter: searchParams.get('consentFilter') || '',
    archiveFilter: searchParams.get('archiveFilter') || 'active',
    certificateStatus: searchParams.get('certificateStatus') || '',
    membershipStatus: searchParams.get('membershipStatus') || ''
  });

  // Dynamic portal consents state & cascading filter
  const [portalConsents, setPortalConsents] = useState([]);
  const initialConsentFilter = searchParams.get('consentFilter') || '';
  const initialParsedConsent = parseConsentFilter(initialConsentFilter);
  const [selectedConsentType, setSelectedConsentType] = useState(initialParsedConsent.consentId);
  const [selectedConsentStatus, setSelectedConsentStatus] = useState(initialParsedConsent.status);

  // Stato separato per il campo di ricerca con debounce
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [searchLoading, setSearchLoading] = useState(false);
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  const defaultRoleOptions = useMemo(
    () => Object.entries(SYSTEM_ROLE_LABELS).map(([value, label]) => ({ value, label })),
    []
  );
  const [roleOptions, setRoleOptions] = useState(defaultRoleOptions);

  // Selected users for broadcast email and bulk actions (stores full user objects)
  const [selectedUsers, setSelectedUsers] = useState([]);
  const selectedUserIds = useMemo(() => new Set(selectedUsers.map(u => u.id)), [selectedUsers]);
  const isAllCurrentPageSelected = useMemo(
    () => users.length > 0 && users.every(u => selectedUserIds.has(u.id)),
    [users, selectedUserIds]
  );

  // Export state
  const [exporting, setExporting] = useState(false);



  // Load users
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userService.getUsers(filters);
      
      if (response.success) {
        const items = response.data.items || [];
        setUsers(items);
        setSelectedUsers(prev => {
          if (prev.length === 0) return prev;
          const itemMap = new Map(items.map(u => [u.id, u]));
          let hasChange = false;
          const updated = prev.map(sel => {
            const fresh = itemMap.get(sel.id);
            if (fresh && fresh !== sel) {
              hasChange = true;
              return fresh;
            }
            return sel;
          });
          return hasChange ? updated : prev;
        });
        setPagination({
          currentPage: response.data.currentPage || 1,
          totalPages: response.data.totalPages || 1,
          totalItems: response.data.totalCount || 0,
          itemsPerPage: filters.limit || 20
        });
      } else {
        setError(response.message || 'Errore nel caricamento degli utenti');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadRoles = useCallback(async () => {
    try {
      const result = await roleService.getAppRoles();
      const baseRoles = result?.success && Array.isArray(result.data)
        ? result.data.map(role => ({
            value: role.name,
            label: role.label || role.name
          }))
        : [];

      const roleMap = new Map();
      Object.entries(SYSTEM_ROLE_LABELS).forEach(([val, lbl]) => {
        roleMap.set(val, { value: val, label: lbl });
      });
      baseRoles.forEach(r => {
        roleMap.set(r.value, r);
      });

      setRoleOptions(Array.from(roleMap.values()));
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoleOptions(
        Object.entries(SYSTEM_ROLE_LABELS).map(([value, label]) => ({
          value,
          label
        }))
      );
    }
  }, []);

  // Sincronizza i filtri nei parametri URL ogni volta che cambiano
  useEffect(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.role) params.role = filters.role;
    if (filters.page && filters.page !== 1) params.page = String(filters.page);
    if (filters.limit && filters.limit !== 20) params.limit = String(filters.limit);
    if (filters.sortBy && filters.sortBy !== 'alphabetical') params.sortBy = filters.sortBy;
    if (filters.lastNameStartsWith) params.letter = filters.lastNameStartsWith;
    if (filters.academicYear !== '') {
      params.academicYear = String(filters.academicYear);
    } else if (
      hasInitializedYearRef.current ||
      initialAcademicYearParam === 'all' ||
      initialAcademicYearParam === '' ||
      searchParams.get('academicYear') === 'all' ||
      searchParams.get('academicYear') === ''
    ) {
      params.academicYear = 'all';
    }
    if (filters.consentFilter) params.consentFilter = filters.consentFilter;
    if (filters.archiveFilter && filters.archiveFilter !== 'active') params.archiveFilter = filters.archiveFilter;
    if (filters.certificateStatus) params.certificateStatus = filters.certificateStatus;
    if (filters.membershipStatus) params.membershipStatus = filters.membershipStatus;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, setSearchParams]);

  // Effect to load users when filters change
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const loadPortalConsents = useCallback(async () => {
    try {
      const result = await portalService.getConsents();
      if (result.success && Array.isArray(result.data)) {
        setPortalConsents(result.data);
      }
    } catch (error) {
      console.error('Error loading portal consents:', error);
    }
  }, []);

  useEffect(() => {
    loadPortalConsents();
  }, [loadPortalConsents]);

  useEffect(() => {
    if (filters.consentFilter) {
      const parsed = parseConsentFilter(filters.consentFilter);
      setSelectedConsentType(parsed.consentId);
      setSelectedConsentStatus(parsed.status);
    }
  }, [filters.consentFilter]);

  useEffect(() => {
    const loadAcademicYears = async () => {
      try {
        setAcademicYearLoading(true);
        const response = await commonService.getCurrentAcademicYear();
        if (response.success && typeof response.data === 'number') {
          const currentYear = response.data;
          const years = [0, 1, 2].map(offset => {
            const year = currentYear - offset;
            return {
              value: year,
              label: `${year}/${year + 1}`
            };
          });
          setAcademicYearOptions(years);
          if (!hasInitializedYearRef.current) {
            hasInitializedYearRef.current = true;
            if (
              initialAcademicYearParam === null &&
              !searchParams.has('certificateStatus') &&
              !searchParams.has('membershipStatus') &&
              searchParams.get('role') !== 'athlete'
            ) {
              setFilters(prev => ({
                ...prev,
                academicYear: currentYear
              }));
            }
          }
        }
      } catch (err) {
        console.error('Error loading academic years:', err);
      } finally {
        setAcademicYearLoading(false);
      }
    };

    loadAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAcademicYearParam]);

  // Debounce per il filtro di ricerca
  useEffect(() => {
    if (searchInput === '' && filters.search === '') {
      return;
    }

    setSearchLoading(true);
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: searchInput,
        page: 1 // Reset to first page on new search
      }));
      setSearchLoading(false);
    }, 500); // 500ms di debounce

    return () => {
      clearTimeout(timeoutId);
      setSearchLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleConsentTypeChange = (newType) => {
    setSelectedConsentType(newType);
    setSelectedConsentStatus('');
    if (filters.consentFilter) {
      handleFilterChange('consentFilter', '');
    }
  };

  const handleConsentStatusChange = (newStatus) => {
    setSelectedConsentStatus(newStatus);
    if (!newStatus || !selectedConsentType) {
      handleFilterChange('consentFilter', '');
      return;
    }
    let filterVal = '';
    if (selectedConsentType === 'mandatory') {
      filterVal = newStatus === 'completed' ? 'mandatory_completed' : 'mandatory_missing';
    } else {
      filterVal = newStatus === 'accepted' ? `consent_${selectedConsentType}_accepted` : `consent_${selectedConsentType}_rejected`;
    }
    handleFilterChange('consentFilter', filterVal);
  };

  useEffect(() => {
    setSelectedUsers([]);
  }, [filters.academicYear]);

  // Handle page change
  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle delete user
  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Sei sicuro di voler eliminare l'utente ${userName}? Questa azione non può essere annullata.`)) {
      try {
        const response = await userService.deleteUser(userId);
        if (response.success) {
          setSuccess('Utente eliminato con successo');
          setSelectedUsers(prev => prev.filter(u => u.id !== userId));
          loadUsers(); // Reload the list
        } else {
          setError('Errore nell\'eliminazione dell\'utente');
        }
      } catch (err) {
        setError('Errore di connessione al server');
        console.error('Error deleting user:', err);
      }
    }
  };

  // Handle archive / unarchive user
  const handleArchiveUser = async (userId, userName, isArchived) => {
    const actionText = isArchived ? 'archiviare' : 'ripristinare';
    if (!window.confirm(`Sei sicuro di voler ${actionText} l'atleta ${userName}?`)) {
      return;
    }

    try {
      const response = await userService.archiveUser(userId, isArchived);
      if (response.success) {
        setSuccess(response.message || (isArchived ? 'Atleta archiviato con successo' : 'Atleta ripristinato con successo'));
        loadUsers();
      } else {
        setError(response.message || `Errore durante ${isArchived ? "l'archiviazione" : 'il ripristino'} dell'atleta`);
      }
    } catch (err) {
      console.error('Error archiving user:', err);
      setError('Errore di connessione al server');
    }
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role) => {
    return ROLE_BADGE_COLORS[role] || 'dark';
  };

  const getUserRoles = (user) => {
    return user.roles;
  };

  // Get role label
  const getRoleLabel = (role) => {
    const roleOption = roleOptions.find(x => x.value === role);
    return roleOption?.label || SYSTEM_ROLE_LABELS[role] || role;
  };

  // Get user groups formatted for badges
  const getUserGroups = (user, selectedAcademicYear) => {
    if (!user?.courseSubscriptions || !Array.isArray(user.courseSubscriptions)) {
      return [];
    }
    const activeSubscriptions = user.courseSubscriptions.filter(
      cs => !cs.isArchived && cs.group && cs.course
    );
    const parsedYear = selectedAcademicYear !== '' && selectedAcademicYear !== null && selectedAcademicYear !== undefined
      ? parseInt(selectedAcademicYear, 10)
      : null;

    const filtered = parsedYear !== null && !Number.isNaN(parsedYear)
      ? activeSubscriptions.filter(cs => cs.course.academicYear === parsedYear)
      : activeSubscriptions;

    const seen = new Set();
    const groups = [];
    for (const cs of filtered) {
      const startYear = cs.course.academicYear;
      const endYearShort = String(startYear + 1).slice(-2);
      const groupName = cs.group.name;
      const key = `${cs.group.id || groupName}-${startYear}`;
      if (!seen.has(key)) {
        seen.add(key);
        groups.push({
          id: cs.group.id,
          name: groupName,
          academicYearLabel: `${startYear}/${endYearShort}`,
          key
        });
      }
    }
    return groups;
  };

  // Handle CSV export (Anagrafiche)
  const handleExportCSV = async () => {
    setExporting(true);
    setError('');
    
    try {
      let exportItems = [];
      if (selectedUsers.length > 0) {
        exportItems = selectedUsers;
      } else {
        // Fetch all users with current filters
        const response = await userService.getUsers({
          ...filters,
          exportAll: true
        });
        
        if (response.success && response.data.items) {
          exportItems = response.data.items;
        } else {
          setError(response.message || 'Errore durante l\'esportazione');
          setExporting(false);
          return;
        }
      }

      const isMandatoryInRegola = (u) => (
        u.consentPrivacy === true &&
        u.consentTesseramento === true &&
        u.consentRegulations === true &&
        u.consentForceMajeure === true &&
        u.consentMedicalDocs === true &&
        u.consentInsuranceTerms === true
      );

      const getMediaConsentLabel = (u) => {
        if (u.consentMediaSocialWhatsapp === true) return 'Accettato';
        return 'Rifiutato';
      };

      const columns = [
        { label: 'Nome', accessor: (u) => u.firstName },
        { label: 'Cognome', accessor: (u) => u.lastName },
        { 
          label: 'Email', 
          accessor: (u) => {
            if (isMinor(u.dateOfBirth, u.fiscalCode)) {
              return (u.parent?.email && u.parent.email.trim()) || u.email || '';
            }
            return u.email || '';
          }
        },
        { label: 'Codice Fiscale', accessor: (u) => u.fiscalCode },
        { 
          label: 'Telefono', 
          accessor: (u) => {
            if (isMinor(u.dateOfBirth, u.fiscalCode)) {
              return (u.parent?.phoneNumber && u.parent.phoneNumber.trim()) || u.phoneNumber || '';
            }
            return u.phoneNumber || '';
          }
        },
        { label: 'Data di Nascita', accessor: (u) => u.dateOfBirth ? moment(u.dateOfBirth).format('DD/MM/YYYY') : '' },
        { label: 'Comune Residenza', accessor: (u) => u.residenceMunicipality || '' },
        { label: 'Provincia', accessor: (u) => u.residenceProvince || '' },
        { label: 'Ruoli', accessor: (u) => getUserRoles(u).map(role => getRoleLabel(role)).join(', ') },
        { label: 'Consensi Obbligatori', accessor: (u) => (isMandatoryInRegola(u) ? 'In regola' : 'Da regolarizzare') },
        { label: 'Liberatoria Foto/Video', accessor: (u) => getMediaConsentLabel(u) },
        { label: 'Data Consensi', accessor: (u) => (u.consentDate ? moment(u.consentDate).format('DD/MM/YYYY HH:mm') : '') },
        { label: 'Data Registrazione', accessor: (u) => moment(u.createdAt).format('DD/MM/YYYY HH:mm') }
      ];
      
      exportToCSV(exportItems, 'utenti', columns);
      setSuccess(`${exportItems.length} anagrafiche esportate con successo`);
    } catch (error) {
      console.error('Export error:', error);
      setError('Errore durante l\'esportazione degli utenti');
    } finally {
      setExporting(false);
    }
  };

  // Handle CSV export (Tesseramenti)
  const handleExportMemberships = async () => {
    setExporting(true);
    setError('');

    try {
      let exportItems = [];
      if (selectedUsers.length > 0) {
        exportItems = selectedUsers;
      } else {
        const response = await userService.getUsers({
          ...filters,
          exportAll: true
        });

        if (response.success && response.data.items) {
          exportItems = response.data.items;
        } else {
          setError(response.message || 'Errore durante l\'esportazione dei tesseramenti');
          setExporting(false);
          return;
        }
      }

      const parsedFilterYear = filters.academicYear !== '' && filters.academicYear !== null && filters.academicYear !== undefined && filters.academicYear !== 'all'
        ? parseInt(filters.academicYear, 10)
        : null;

      const getTargetMembership = (u) => {
        const memberships = Array.isArray(u.memberships) ? u.memberships : [];
        if (memberships.length === 0) return null;

        if (parsedFilterYear !== null && !Number.isNaN(parsedFilterYear)) {
          return memberships.find(m => m.academicYear === parsedFilterYear) || null;
        }

        const sorted = [...memberships].sort((a, b) => {
          if ((b.academicYear || 0) !== (a.academicYear || 0)) {
            return (b.academicYear || 0) - (a.academicYear || 0);
          }
          const dateB = new Date(b.createdAt || b.paidDate || 0).getTime();
          const dateA = new Date(a.createdAt || a.paidDate || 0).getTime();
          return dateB - dateA;
        });
        return sorted[0] || null;
      };

      const formatMembershipDate = (dateVal) => {
        if (!dateVal) return '';
        const m = moment(dateVal);
        return m.isValid() ? m.format('DD/MM/YYYY') : '';
      };

      const formatAcademicYearLabel = (year) => {
        if (year === null || year === undefined || year === '') return '';
        const num = parseInt(year, 10);
        return !Number.isNaN(num) ? `${num}/${num + 1}` : '';
      };

      const columns = [
        { label: 'Nome', accessor: (u) => u.firstName || '' },
        { label: 'Cognome', accessor: (u) => u.lastName || '' },
        { 
          label: 'Numero Tessera', 
          accessor: (u) => {
            const m = getTargetMembership(u);
            return m?.membershipNumber || '';
          }
        },
        { 
          label: 'Scadenza Tessera', 
          accessor: (u) => {
            const m = getTargetMembership(u);
            return formatMembershipDate(m?.expiryDate);
          }
        },
        { 
          label: 'Data Pagamento', 
          accessor: (u) => {
            const m = getTargetMembership(u);
            return formatMembershipDate(m?.paidDate);
          }
        },
        { 
          label: 'Anno Sportivo', 
          accessor: (u) => {
            const m = getTargetMembership(u);
            return m ? formatAcademicYearLabel(m.academicYear) : '';
          }
        }
      ];

      exportToCSV(exportItems, 'tesseramenti', columns);
      setSuccess(`${exportItems.length} dati di tesseramento esportati con successo`);
    } catch (error) {
      console.error('Export memberships error:', error);
      setError('Errore durante l\'esportazione dei dati di tesseramento');
    } finally {
      setExporting(false);
    }
  };

  // Copy public registration link to clipboard
  const handleCopyRegistrationLink = async () => {
    try {
      const publicUrl = `${window.location.origin}/register`;
      await navigator.clipboard.writeText(publicUrl);
      setSuccess('Link di registrazione copiato negli appunti!');
    } catch (err) {
      console.error('Failed to copy registration link:', err);
      setError('Impossibile copiare il link negli appunti.');
    }
  };

  // Handle select/deselect individual user
  const handleSelectUser = (userOrId) => {
    const userId = typeof userOrId === 'object' && userOrId !== null ? userOrId.id : userOrId;
    const userObj = typeof userOrId === 'object' && userOrId !== null ? userOrId : users.find(u => u.id === userId);

    setSelectedUsers(prev => {
      if (prev.some(u => u.id === userId)) {
        return prev.filter(u => u.id !== userId);
      } else if (userObj) {
        return [...prev, userObj];
      }
      return prev;
    });
  };

  // Handle select/deselect all users on current page
  const handleSelectAll = () => {
    if (isAllCurrentPageSelected) {
      // Deselect all users on current page, keeping selections on other pages
      const currentPageIds = new Set(users.map(u => u.id));
      setSelectedUsers(prev => prev.filter(u => !currentPageIds.has(u.id)));
    } else {
      // Select all users on current page, avoiding duplicates
      setSelectedUsers(prev => {
        const existingIds = new Set(prev.map(u => u.id));
        const toAdd = users.filter(u => !existingIds.has(u.id));
        return [...prev, ...toAdd];
      });
    }
  };

  return (
    <div className="user-list fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gestione Anagrafiche</h2>
        <div>
          <BulkUserActions
            selectedUsers={selectedUsers}
            onSuccess={(msg) => setSuccess(msg)}
            onError={(err) => setError(err)}
            onClearSelection={() => setSelectedUsers([])}
            className="me-2"
          />
          <Dropdown className="d-inline-block me-2">
            <Dropdown.Toggle 
              variant="outline-success" 
              id="dropdown-export"
              disabled={loading || exporting}
            >
              {exporting ? (
                <>
                  <Spinner 
                    as="span" 
                    animation="border" 
                    size="sm" 
                    className="me-2" 
                    role="status" 
                    aria-hidden="true"
                  />
                  Esportazione in corso...
                </>
              ) : (
                <>
                  <i className="fas fa-file-export me-2"></i>
                  Esporta
                </>
              )}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={handleExportCSV}>
                <i className="fas fa-file-csv me-2 text-success"></i>
                Esporta Anagrafiche
              </Dropdown.Item>
              <Dropdown.Item onClick={handleExportMemberships}>
                <i className="fas fa-id-card me-2 text-primary"></i>
                Estrai Tesseramenti
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button 
            variant="outline-primary" 
            className="me-2"
            onClick={handleCopyRegistrationLink}
            title="Copia il link di registrazione pubblica negli appunti"
          >
            <i className="fas fa-copy me-2"></i>
            Copia link registrazione
          </Button>
          <Link to="/users/new" className="btn btn-primary">
            <i className="fas fa-plus me-2"></i>
            Nuovo Utente
          </Link>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Filters */}
      {/* Alphabet Bar */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <div className="d-flex flex-wrap gap-1 align-items-center justify-content-center">
            <Button
              variant={filters.lastNameStartsWith === '' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, lastNameStartsWith: '', page: 1 }))}
              style={{ minWidth: '42px', fontWeight: 600 }}
            >
              Tutti
            </Button>
            {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
              <Button
                key={letter}
                variant={filters.lastNameStartsWith === letter ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, lastNameStartsWith: letter, page: 1 }))}
                style={{ minWidth: '34px', fontWeight: 600 }}
              >
                {letter}
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={6} lg={3}>
              <Form.Group controlId="search">
                <Form.Label>Cerca</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Nome, cognome, email o codice fiscale..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchLoading && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group controlId="role">
                <Form.Label>Ruolo</Form.Label>
                <Form.Select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <option value="">Tutti i ruoli</option>
                  {roleOptions.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group controlId="academicYear">
                <Form.Label>Anno Sportivo</Form.Label>
                <Form.Select
                  value={filters.academicYear === '' ? '' : String(filters.academicYear)}
                  onChange={(e) => handleFilterChange('academicYear', e.target.value ? parseInt(e.target.value, 10) : '')}
                  disabled={academicYearLoading}
                >
                  <option value="">Tutti</option>
                  {academicYearOptions.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group controlId="membershipStatusFilter">
                <Form.Label>Tesseramento</Form.Label>
                <Form.Select
                  aria-label="Tesseramento"
                  value={filters.membershipStatus}
                  onChange={(e) => handleFilterChange('membershipStatus', e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="missing">Senza tesseramento</option>
                  <option value="all">Tesserato (Tutti)</option>
                  <option value="paid">Tesserato (Pagato)</option>
                  <option value="unpaid">Tesserato (Non pagato)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group controlId="certificateStatusFilter">
                <Form.Label>Certificato Medico</Form.Label>
                <Form.Select
                  aria-label="Certificato Medico"
                  value={filters.certificateStatus}
                  onChange={(e) => handleFilterChange('certificateStatus', e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="missing">Mancante</option>
                  <option value="expiring">In scadenza</option>
                  <option value="expired">Scaduto</option>
                  <option value="valid">Valido</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={2}>
              <Form.Group controlId="consentTypeFilter">
                <Form.Label>Consensi</Form.Label>
                <Form.Select
                  aria-label="Consensi"
                  value={selectedConsentType}
                  onChange={(e) => handleConsentTypeChange(e.target.value)}
                >
                  <option value="">Tutti i consensi</option>
                  <option value="mandatory">Consensi obbligatori</option>
                  {portalConsents
                    .filter(c => c.isEnabled && !c.isMandatory)
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {selectedConsentType && (
              <Col md={6} lg={2}>
                <Form.Group controlId="consentStatusFilter">
                  <Form.Label>Stato</Form.Label>
                  <Form.Select
                    aria-label="Stato consenso"
                    value={selectedConsentStatus}
                    onChange={(e) => handleConsentStatusChange(e.target.value)}
                  >
                    <option value="">Seleziona stato...</option>
                    {selectedConsentType === 'mandatory' ? (
                      <>
                        <option value="completed">In regola</option>
                        <option value="missing">Da regolarizzare</option>
                      </>
                    ) : (
                      <>
                        <option value="accepted">Accettato</option>
                        <option value="rejected">Rifiutato</option>
                      </>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={6} lg={2}>
              <Form.Group controlId="sortBy">
                <Form.Label>Ordina per</Form.Label>
                <Form.Select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  <option value="alphabetical">Nome (A→Z)</option>
                  <option value="createdAt">Data Registrazione (↓)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} lg={1}>
              <Form.Group controlId="limit">
                <Form.Label>Per Pagina</Form.Label>
                <Form.Select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/* Users Table */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center bg-white py-3">
          <div className="fw-bold text-muted">
            <i className="fas fa-users me-2"></i>
            Elenco Atleti
          </div>
          <div className="btn-group btn-group-sm" role="group" aria-label="Filtro archivio">
            <button
              type="button"
              className={`btn ${filters.archiveFilter === 'active' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleFilterChange('archiveFilter', 'active')}
            >
              Attivi
            </button>
            <button
              type="button"
              className={`btn ${filters.archiveFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleFilterChange('archiveFilter', 'all')}
            >
              Tutti
            </button>
            <button
              type="button"
              className={`btn ${filters.archiveFilter === 'archived' ? 'btn-warning' : 'btn-outline-secondary'}`}
              onClick={() => handleFilterChange('archiveFilter', 'archived')}
            >
              <i className="fas fa-archive me-1"></i>
              Archiviati
            </button>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="loading-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Caricamento...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th style={{width: '50px'}}>
                      <Form.Check 
                        type="checkbox"
                        checked={isAllCurrentPageSelected}
                        onChange={handleSelectAll}
                        disabled={users.length === 0}
                      />
                    </th>
                    <th>Nome Completo</th>
                    <th>Gruppi</th>
                    <th>Ruolo</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user) => {
                      const userGroups = getUserGroups(user, filters.academicYear);
                      return (
                        <tr key={user.id} className={user.isArchived ? 'table-light text-muted' : ''}>
                          <td>
                            <Form.Check 
                              type="checkbox"
                              checked={selectedUserIds.has(user.id)}
                              onChange={() => handleSelectUser(user)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center flex-wrap">
                              <Link 
                                to={`/users/${user.id}/edit`}
                                className="text-decoration-none"
                              >
                                <strong className={user.isArchived ? 'text-secondary' : 'text-primary'}>
                                  {user.firstName} {user.lastName}
                                </strong>
                              </Link>
                              {user.isArchived && (
                                <Badge bg="warning" text="dark" className="ms-2">
                                  <i className="fas fa-archive me-1"></i>
                                  Archiviato
                                </Badge>
                              )}
                            </div>
                            {user.dateOfBirth && (
                              <div className="text-muted small">
                                {moment(user.dateOfBirth).format('DD/MM/YYYY')}
                              </div>
                            )}
                          </td>
                          <td>
                            {userGroups.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {userGroups.map(group => (
                                  <Badge 
                                    key={`${user.id}-${group.key}`}
                                    bg="info"
                                    className="badge-role"
                                  >
                                    {group.name} ({group.academicYearLabel})
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {getUserRoles(user).map(role => (
                                <Badge 
                                  key={`${user.id}-${role}`}
                                  bg={getRoleBadgeVariant(role)}
                                  className="badge-role"
                                >
                                  {getRoleLabel(role)}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Link
                                to={`/users/${user.id}/edit`}
                                className="btn btn-outline-primary btn-action"
                                title="Modifica"
                              >
                                <i className="fas fa-edit"></i>
                              </Link>
                              {isCurrentUserAdmin && (
                                user.isArchived ? (
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    className="btn-action"
                                    title="Ripristina Atleta"
                                    onClick={() => handleArchiveUser(user.id, `${user.firstName} ${user.lastName}`, false)}
                                  >
                                    <i className="fas fa-undo"></i>
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="btn-action"
                                    title="Archivia Atleta"
                                    onClick={() => handleArchiveUser(user.id, `${user.firstName} ${user.lastName}`, true)}
                                  >
                                    <i className="fas fa-archive"></i>
                                  </Button>
                                )
                              )}
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="btn-action"
                                title="Elimina"
                                onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        Nessun utente trovato
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Totale {pagination?.totalItems || users.length} risultati
                  </div>
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={pagination?.currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange((pagination?.currentPage || 1) - 1)}
                      disabled={pagination?.currentPage === 1}
                    />
                    
                    {[...Array(pagination?.totalPages || 1)].map((_, index) => {
                      const page = index + 1;
                      if (
                        page === 1 ||
                        page === pagination?.totalPages ||
                        (page >= (pagination?.currentPage || 1) - 2 && page <= (pagination?.currentPage || 1) + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === pagination?.currentPage}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      } else if (
                        page === (pagination?.currentPage || 1) - 3 ||
                        page === (pagination?.currentPage || 1) + 3
                      ) {
                        return <Pagination.Ellipsis key={page} />;
                      }
                      return null;
                    })}

                    <Pagination.Next
                      onClick={() => handlePageChange((pagination?.currentPage || 1) + 1)}
                      disabled={pagination?.currentPage === pagination?.totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(pagination?.totalPages || 1)}
                      disabled={pagination?.currentPage === pagination?.totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserList;
